import { computed, ref, watch } from 'vue';
import { updatePersonalSurveyProfile } from '@/api/personalSurveyProfile';
import {
  trackAdditionalBusinessContextSuggestionEvent,
  trackInsightEvent,
} from '@/api/tracking';
import { resolveInsightGenerationFailure } from '@/components/home/insights/insight-generation-failure';
import { resolveInsightGenerationPhase } from '@/components/home/insights/insight-generation-phase';
import { mergeInsightSources } from '@/components/home/insights/merge-insight-sources';
import { provideInsightAxisRetry } from '@/components/home/insights/use-insight-axis-retry';
import { provideInsightPrefillStatuses } from '@/components/home/insights/use-insight-prefill-statuses';
import {
  provideInsightDate,
  provideInsightId,
  provideInsightPageName,
} from '@/components/home/insights/use-insight-tracking';
import { useInsightsAvailability } from '@/components/home/insights/use-insights-availability';
import { useInsightsProgress } from '@/components/home/insights/use-insights-progress';
import type {
  AdditionalBusinessContextSuggestionRefreshFrom,
  PersonalSurveyProfileInput,
} from '@/types';
import type { InsightTrackingPageName } from '@/types/insights';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useInsightAxisErrorNotice } from '@/utils/composables/useInsightAxisErrorNotice';
import { useInsightPrefillPromptStatuses } from '@/utils/composables/useInsightPrefillPromptStatuses';
import { useInsightRegenerateBannerDismissal } from '@/utils/composables/useInsightRegenerateBannerDismissal';
import { useInsightsGeneration } from '@/utils/composables/useInsightsGeneration';
import { useInsightSourcesPolling } from '@/utils/composables/useInsightSourcesPolling';
import { useInsightViewedTracking } from '@/utils/composables/useInsightViewedTracking';
import { useLayoutVariant } from '@/utils/composables/useLayoutVariant';
import {
  toPersonalSurveyProfileInput,
  toProfileTrackingCategory,
} from '@/utils/personalSurveyProfile';
import { featureFlags } from '@/featureFlags';

/**
 * 発想のヒントのコンテナが子コンポーネントへ渡す状態をまとめる。
 * 表示（レイアウト・文言）は呼び出し側のコンポーネントが持つ。
 */
export const useInsightsContainer = ({
  pageName,
}: {
  pageName: InsightTrackingPageName;
}) => {
  provideInsightPageName(pageName);

  const {
    hasLoaded,
    userProfileEnabled,
    isInsightEnabled,
    hasProfile,
    hasSetupPersonalNewsOrFavoriteThemes,
    hasPersonalNewsArticles,
    hasFeaturedArticles,
    personalSurveyProfile,
    mutateProfile,
  } = useInsightsAvailability();

  const isOpen = ref(false);
  const layoutVariant = useLayoutVariant();

  const {
    data,
    isGenerating,
    isRegenerating,
    retry,
    regenerate,
    regeneratable,
    mutate,
  } = useInsightsGeneration(isInsightEnabled);
  provideInsightId(() => data.value?.id ?? null);
  provideInsightDate(() => data.value?.date ?? null);

  const phase = computed(() =>
    resolveInsightGenerationPhase(data.value, isRegenerating.value),
  );
  const isAwaitingFirstAxis = computed(() => phase.value.isAwaitingFirstAxis);
  const canOpen = computed(() => phase.value.canOpen);
  const isFullyGenerated = computed(() => phase.value.isFullyGenerated);

  const insightUuid = computed(() => data.value?.uuid ?? null);
  const { isViewed, markAsViewed } = useInsightViewedTracking(insightUuid);

  const isUnviewed = computed(
    () =>
      (isFullyGenerated.value || !hasProfile.value) &&
      !isViewed.value &&
      !isOpen.value,
  );
  const isProcessedAndViewed = computed(
    () => isFullyGenerated.value && isViewed.value,
  );

  // 開いている間に全軸が確定したら既読にする。開いた瞬間だけでなく、
  // 開いたまま再生成して uuid が変わった場合や、開いたまま残りの軸が確定した場合も対象にする
  watch(
    [isOpen, isFullyGenerated, insightUuid],
    ([open, generated]) => {
      if (open && generated) markAsViewed();
    },
    { immediate: true },
  );

  const onProfileSaved = async () => {
    await mutateProfile();
    await mutate();
    if (data.value && data.value.regeneratable) {
      await regenerate();
    }
  };

  const { getCurrentPathForTracking } = useCurrentPathForTracking();
  const canAdjustHints = computed(
    () =>
      featureFlags.ANDEV_10700_INSIGHT_ADDITIONAL_BUSINESS_CONTEXT &&
      hasProfile.value,
  );
  const isProfileModalOpen = ref(false);
  const isSavingProfile = ref(false);
  const hasProfileSaveError = ref(false);
  // 登録の保存中に業務情報を再取得すると hasProfile が true に変わるため、
  // 開いた時点の種別で固定し、閉じるまでタイトルが「ヒントを調整」に切り替わらないようにする。
  const profileModalVariant = ref<'adjust' | 'register'>('adjust');
  const profileInputAtAdjustStart = ref<PersonalSurveyProfileInput | null>(
    null,
  );
  const suggestionApplyCount = ref(0);
  watch(isProfileModalOpen, open => {
    if (!open) return;
    hasProfileSaveError.value = false;
    suggestionApplyCount.value = 0;
    profileModalVariant.value = hasProfile.value ? 'adjust' : 'register';
    profileInputAtAdjustStart.value = personalSurveyProfile.value
      ? toPersonalSurveyProfileInput(personalSurveyProfile.value)
      : null;
    // 登録モーダルはヒントの調整ではないので、調整開始イベントは送らない。
    if (profileModalVariant.value !== 'adjust') return;
    const insightId = data.value?.id;
    if (typeof insightId !== 'number') return;
    void trackInsightEvent('insight_adjustment_start', {
      page: { name: pageName },
      insight: { id: insightId },
      layout_variant: layoutVariant,
    });
  });
  const saveProfile = async (input: PersonalSurveyProfileInput) => {
    const isRegistering = profileModalVariant.value === 'register';
    isSavingProfile.value = true;
    hasProfileSaveError.value = false;
    const sentSuggestionApplyCount = suggestionApplyCount.value;
    try {
      await updatePersonalSurveyProfile(
        input,
        {
          pageName,
          pageUrl: getCurrentPathForTracking(),
          feature: 'insight',
          layoutVariant,
        },
        profileInputAtAdjustStart.value
          ? toProfileTrackingCategory(
              profileInputAtAdjustStart.value,
              input,
              sentSuggestionApplyCount,
            )
          : undefined,
      );
      suggestionApplyCount.value -= sentSuggestionApplyCount;
      if (isRegistering) {
        await onProfileSaved();
      } else {
        await mutateProfile();
        await regenerate({
          page: { name: pageName },
          layout_variant: layoutVariant,
        });
      }
      isProfileModalOpen.value = false;
    } catch {
      hasProfileSaveError.value = true;
    } finally {
      isSavingProfile.value = false;
    }
  };

  const onSuggestionApply = () => {
    suggestionApplyCount.value += 1;
    void trackAdditionalBusinessContextSuggestionEvent(
      'additional_business_context_suggestion_apply',
      { page: { name: pageName }, layout_variant: layoutVariant },
    );
  };
  const onSuggestionRefresh = (
    from: AdditionalBusinessContextSuggestionRefreshFrom,
  ) => {
    void trackAdditionalBusinessContextSuggestionEvent(
      'additional_business_context_suggestion_refresh',
      {
        page: { name: pageName },
        layout_variant: layoutVariant,
        context: { from },
      },
    );
  };

  const toggleOpen = () => {
    isOpen.value = !isOpen.value;
    const insightId = data.value?.id;
    if (typeof insightId !== 'number') return;
    trackInsightEvent(isOpen.value ? 'insight_expand' : 'insight_collapse', {
      insight: { id: insightId },
      layout_variant: layoutVariant,
    });
  };

  const onRegenerate = async () => {
    const insightId = data.value?.id;
    await regenerate();
    if (typeof insightId !== 'number') return;
    await trackInsightEvent('insight_regenerate', {
      page: { name: pageName },
      insight: { id: insightId },
      layout_variant: layoutVariant,
    });
  };
  provideInsightAxisRetry(onRegenerate);
  useInsightAxisErrorNotice({ insight: data, isOpen, retry: onRegenerate });

  const failure = computed(() =>
    resolveInsightGenerationFailure(data.value, isRegenerating.value),
  );
  const hasGenerationError = computed(() => failure.value === 'initial');
  const hasRegenerationError = computed(() => failure.value === 'regeneration');

  const profileUpdatedAt = computed(
    () => personalSurveyProfile.value?.updated_at,
  );
  const { isDismissed, dismiss } =
    useInsightRegenerateBannerDismissal(profileUpdatedAt);
  const showRegenerateBanner = computed(
    () =>
      regeneratable.value &&
      !isGenerating.value &&
      !isDismissed.value &&
      !hasRegenerationError.value,
  );

  const {
    progress,
    progressBarWidth,
    durationLabel,
    showProgress,
    isComplete,
  } = useInsightsProgress({
    // 残りの軸の生成中も進捗バーを出し続けると公開済みの軸を読めないので、最初の軸の公開で進捗バーを終える
    isGenerating: isAwaitingFirstAxis,
    hasError: computed(() => failure.value !== null),
    // 再生成した後の場合、最初のGETは前のprocessedの発想のヒントなのでcreated_atを無視してプログレスを0からにする
    createdAt: computed(() =>
      isRegenerating.value ? undefined : data.value?.created_at,
    ),
    isRestarting: isRegenerating,
  });

  const { statuses: prefillPromptStatuses } =
    useInsightPrefillPromptStatuses(data);
  provideInsightPrefillStatuses(prefillPromptStatuses);

  const { sources: latestSources } = useInsightSourcesPolling(data);
  const payload = computed(() => {
    const snapshot = data.value?.payload;
    if (snapshot === undefined) return undefined;
    if (latestSources.value === undefined) return snapshot;
    return mergeInsightSources(snapshot, latestSources.value);
  });

  const errorTitle = computed(() =>
    data.value?.latest_attempt.error_message === 'timeout'
      ? '発想のヒントの生成に失敗しました'
      : '現在、発想のヒントを生成することができません',
  );
  const errorMessage = computed(() =>
    data.value?.latest_attempt.error_message === 'timeout'
      ? '申し訳ありませんが、再度お試しください'
      : '申し訳ありませんが、時間を空けて再度お試しください',
  );

  return {
    hasLoaded,
    userProfileEnabled,
    isInsightEnabled,
    hasProfile,
    personalSurveyProfile,
    canAdjustHints,
    profileModalVariant,
    isProfileModalOpen,
    isSavingProfile,
    hasProfileSaveError,
    saveProfile,
    onSuggestionApply,
    onSuggestionRefresh,
    payload,
    hasSetupPersonalNewsOrFavoriteThemes,
    hasPersonalNewsArticles,
    hasFeaturedArticles,
    data,
    isGenerating,
    isAwaitingFirstAxis,
    canOpen,
    isOpen,
    isUnviewed,
    isProcessedAndViewed,
    toggleOpen,
    onProfileSaved,
    onRegenerate,
    retry,
    hasGenerationError,
    hasRegenerationError,
    showRegenerateBanner,
    dismiss,
    progress,
    progressBarWidth,
    durationLabel,
    showProgress,
    isComplete,
    errorTitle,
    errorMessage,
  };
};
